// Знак, локап и аватар. SMY-66
//
// Знак строится геометрией, а не картинкой: так он остаётся чётким на любом
// размере и красится токенами. Готовые файлы в design/exports нужны для
// сторов и фавикона — там цвет зашит hex'ом, и в интерфейсе это было бы
// вторым источником истины.
//
// Пропорции из таблицы «Логотип и знак»: бар со скруглением `r r r 2`,
// первый ink, второй accent. Отношения взяты у знака 30×22 (бар 13, зазор 4,
// радиус 7, хвост 2) и масштабируются от высоты.

import { Icon } from './Icon.tsx'

export function Mark({ height = 22, inverse = false }: { height?: number; inverse?: boolean }) {
  const bar = (height * 13) / 22
  const gap = (height * 4) / 22
  const r = (height * 7) / 22
  const tail = (height * 2) / 22
  const width = bar * 2 + gap

  const quote = (x: number, fill: string) => (
    <path
      key={x}
      d={`M${x + bar - r},0 A${r},${r} 0 0 1 ${x + bar},${r} V${height - r} A${r},${r} 0 0 1 ${x + bar - r},${height} H${x + tail} A${tail},${tail} 0 0 1 ${x},${height - tail} V${r} A${r},${r} 0 0 1 ${x + r},0 Z`}
      fill={fill}
    />
  )

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Смыслика">
      {quote(0, inverse ? 'var(--surface)' : 'var(--ink)')}
      {/* Инверсия: на тёмном второй бар светлее, иначе он тонет в фоне. */}
      {quote(bar + gap, inverse ? 'oklch(0.62 0.17 258)' : 'var(--accent)')}
    </svg>
  )
}

/**
 * Локап: знак плюс слово строчными. Зазор 11–12px по таблице.
 *
 * Слово набирается текстом, а не картинкой, — так его читают поисковики и
 * программы чтения с экрана, и оно не мылится.
 */
export function Lockup({ height = 22, inverse = false }: { height?: number; inverse?: boolean }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 11 }}>
      <Mark height={height} inverse={inverse} />
      <span
        style={{
          font: 'var(--text-logo)',
          fontSize: Math.round(height * 1.1),
          letterSpacing: 'var(--tracking-logo)',
          color: inverse ? 'var(--surface)' : 'var(--ink)',
        }}
      >
        смыслика
      </span>
    </span>
  )
}

/**
 * Фотография или инициалы в круге. Экраны 02 и 11; цвета из разбора экрана 02.
 *
 * Три состояния, и порядок между ними такой: фотография, инициалы, силуэт.
 * Ни имени, ни фото может не быть: имя — третий и ПРОПУСКАЕМЫЙ шаг
 * регистрации, а фото не спрашивают вовсе. Тогда в круге силуэт, а не буква:
 * любая подставленная буква выдавала бы за имя пользователя что-то, чем оно
 * не является.
 */
export function Avatar({ name, src, size = 30 }: { name?: string; src?: string; size?: number }) {
  const initials = (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')

  if (src != null) {
    return (
      <img
        // Ссылка подписанная и живёт минуты; протухнет — браузер покажет
        // сломанную картинку. Пустой alt оставляет на её месте ничего:
        // фотография здесь украшение, а имя стоит рядом текстом.
        alt=""
        src={src}
        width={size}
        height={size}
        style={{ flex: 'none', width: size, height: size, borderRadius: '50%', objectFit: 'cover' }}
      />
    )
  }

  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 'none',
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'oklch(0.91 0.045 258)',
        color: 'oklch(0.36 0.13 258)',
        font: 'var(--text-caps)',
        fontSize: Math.round(size * 0.4),
        letterSpacing: 0,
        textTransform: 'none',
      }}
    >
      {initials === '' ? <Icon name="profile" size={Math.round(size * 0.58)} /> : initials}
    </span>
  )
}
